import { useEffect, useState } from "react";
import API from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Users, Copy, LogOut, Trophy, Plus } from "lucide-react";
import { toast } from "sonner";

/**
 * Squad card for the Step-a-Thon rewards page. Handles the three states:
 *   1. Not in a squad → show Create + Join-by-code UI
 *   2. In a squad → show member list, invite code (tap to copy), leave
 *   3. Below the card: squad-vs-squad leaderboard (week/month/lifetime)
 */
const WINDOW_OPTS = [
  { id: "week",  label: "Week" },
  { id: "month", label: "Month" },
  { id: "all",   label: "Lifetime" },
];

export default function SquadCard() {
  const [squad, setSquad] = useState(null);
  const [members, setMembers] = useState([]);
  const [lb, setLb] = useState(null);
  const [lbWindow, setLbWindow] = useState("all");
  const [creating, setCreating] = useState(false);
  const [joining, setJoining] = useState(false);
  const [newName, setNewName] = useState("");
  const [newEmoji, setNewEmoji] = useState("🚶");
  const [joinCode, setJoinCode] = useState("");

  const loadSquad = async () => {
    try {
      const r = await API.get("/squads/mine");
      setSquad(r.data?.squad || null);
      setMembers(r.data?.members || []);
    } catch { /* no squad */ }
  };

  const loadLeaderboard = async (win) => {
    try {
      const r = await API.get(`/squads/leaderboard?window=${win}`);
      setLb(r.data);
    } catch { setLb(null); }
  };

  useEffect(() => { loadSquad(); }, []);
  useEffect(() => { loadLeaderboard(lbWindow); }, [lbWindow, squad?.id]);

  const createSquad = async () => {
    if (newName.trim().length < 2) return toast.error("Give your squad a name (2+ chars).");
    setCreating(true);
    try {
      const r = await API.post("/squads", { name: newName.trim(), avatar_emoji: newEmoji });
      setSquad(r.data.squad);
      setMembers(r.data.members);
      toast.success(`Squad "${r.data.squad.name}" created! Share code ${r.data.squad.invite_code} to invite friends.`);
      setNewName("");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't create squad.");
    } finally {
      setCreating(false);
    }
  };

  const joinSquad = async () => {
    const code = joinCode.trim().toUpperCase();
    if (code.length !== 6) return toast.error("Invite codes are 6 characters.");
    setJoining(true);
    try {
      const r = await API.post("/squads/join", { invite_code: code });
      setSquad(r.data.squad);
      setMembers(r.data.members);
      toast.success(`Joined "${r.data.squad.name}"!`);
      setJoinCode("");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't join squad.");
    } finally {
      setJoining(false);
    }
  };

  const leaveSquad = async () => {
    if (!confirm(`Leave "${squad.name}"?`)) return;
    try {
      await API.post(`/squads/${squad.id}/leave`);
      toast.success("Left the squad.");
      setSquad(null);
      setMembers([]);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't leave.");
    }
  };

  const copyCode = () => {
    if (!squad?.invite_code) return;
    navigator.clipboard.writeText(squad.invite_code);
    toast.success("Invite code copied!");
  };

  return (
    <Card data-testid="squad-card" className="border-purple-300/40 bg-white/5">
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-white">
          <Users className="w-5 h-5 text-purple-300" />
          Your Squad
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {squad ? (
          <>
            <div className="flex items-center gap-3">
              <div className="text-4xl">{squad.avatar_emoji}</div>
              <div className="flex-1 min-w-0">
                <div className="font-extrabold text-white text-lg" data-testid="squad-name">{squad.name}</div>
                <div className="text-xs text-purple-100/70">{squad.member_count}/10 members</div>
              </div>
              <button
                onClick={copyCode}
                data-testid="squad-copy-code"
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-purple-500/20 hover:bg-purple-500/30 border border-purple-300/40 font-mono font-bold text-purple-100 text-sm"
                title="Tap to copy"
              >
                <Copy className="w-3.5 h-3.5" />
                {squad.invite_code}
              </button>
            </div>
            <div className="flex flex-wrap gap-1.5">
              {members.map((m) => (
                <span key={m.user_id} className="px-2 py-0.5 rounded-full bg-white/10 text-white text-xs font-semibold">
                  {m.first_name}
                </span>
              ))}
            </div>
            <Button
              onClick={leaveSquad}
              variant="outline"
              size="sm"
              className="rounded-full text-xs border-white/20 text-white hover:bg-white/10"
              data-testid="squad-leave"
            >
              <LogOut className="w-3.5 h-3.5 mr-1" /> Leave squad
            </Button>
          </>
        ) : (
          <>
            <p className="text-sm text-purple-100/80">
              Form a squad of up to 10 friends and compete on private leaderboards — plus enter Step-a-Thon events as a team.
            </p>
            <div className="grid gap-3 sm:grid-cols-2">
              <div className="rounded-2xl border border-purple-300/30 p-3 space-y-2">
                <div className="font-bold text-white text-sm flex items-center gap-1.5">
                  <Plus className="w-4 h-4" /> Create a squad
                </div>
                <Input value={newName} onChange={(e) => setNewName(e.target.value)} placeholder="Squad name" maxLength={32} className="rounded-full" data-testid="squad-name-input" />
                <div className="flex gap-2 items-center">
                  <Input value={newEmoji} onChange={(e) => setNewEmoji(e.target.value)} placeholder="🚶" maxLength={4} className="rounded-full w-20 text-center" data-testid="squad-emoji-input" />
                  <Button onClick={createSquad} disabled={creating} className="flex-1 rounded-full bg-purple-600 hover:bg-purple-700" data-testid="squad-create-btn">
                    {creating ? "Creating…" : "Create"}
                  </Button>
                </div>
              </div>
              <div className="rounded-2xl border border-purple-300/30 p-3 space-y-2">
                <div className="font-bold text-white text-sm flex items-center gap-1.5">
                  <Users className="w-4 h-4" /> Join by code
                </div>
                <Input value={joinCode} onChange={(e) => setJoinCode(e.target.value.toUpperCase())} placeholder="ABC123" maxLength={6} className="rounded-full font-mono text-center uppercase" data-testid="squad-join-code-input" />
                <Button onClick={joinSquad} disabled={joining} variant="secondary" className="w-full rounded-full" data-testid="squad-join-btn">
                  {joining ? "Joining…" : "Join squad"}
                </Button>
              </div>
            </div>
          </>
        )}

        {lb?.top && lb.top.length > 0 && (
          <div className="mt-5 pt-4 border-t border-white/10">
            <div className="flex items-center gap-3 mb-3">
              <div className="flex items-center gap-1.5 font-bold text-white text-sm">
                <Trophy className="w-4 h-4 text-amber-300" /> Squad leaderboard
              </div>
              <div
                data-testid="squad-window-toggle"
                className="ml-auto inline-flex rounded-full bg-white/5 border border-purple-300/30 p-0.5 text-[11px] font-bold"
              >
                {WINDOW_OPTS.map((opt) => (
                  <button
                    key={opt.id}
                    type="button"
                    onClick={() => setLbWindow(opt.id)}
                    className={`px-2.5 py-0.5 rounded-full transition-colors ${
                      lbWindow === opt.id ? "bg-purple-500 text-white" : "text-purple-100/70 hover:text-white"
                    }`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>
            <div className="space-y-1">
              {lb.top.map((row) => (
                <div
                  key={row.squad_id}
                  className={`flex items-center gap-3 px-3 py-2 rounded-xl ${row.is_mine ? "bg-purple-500/25 border border-purple-400/40" : "bg-white/5"}`}
                  data-testid={`squad-leaderboard-row-${row.rank}`}
                >
                  <div className={`w-7 h-7 rounded-full flex items-center justify-center font-black text-xs ${
                    row.rank === 1 ? "bg-yellow-400 text-yellow-950" :
                    row.rank === 2 ? "bg-slate-300 text-slate-900" :
                    row.rank === 3 ? "bg-amber-600 text-amber-50" :
                    "bg-white/10 text-white"
                  }`}>
                    {row.rank}
                  </div>
                  <div className="text-2xl">{row.avatar_emoji}</div>
                  <div className="flex-1 min-w-0">
                    <div className="font-bold text-white text-sm truncate">
                      {row.name}
                      {row.is_mine && <span className="ml-1.5 text-[10px] text-pink-300 uppercase tracking-wider">Yours</span>}
                    </div>
                    <div className="text-[10px] text-purple-100/60">{row.member_count} member{row.member_count === 1 ? "" : "s"}</div>
                  </div>
                  <div className="tabular-nums font-mono text-sm text-purple-100/90">
                    {row.total_steps.toLocaleString()} <span className="text-[10px] opacity-70">steps</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
